import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, TextField, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TablePagination, Checkbox, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/Delete';

import { licensingWhitelistAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '-');
const REGIONS = new Intl.DisplayNames(['en'], { type: 'region' });
const countryName = (code) => { try { return REGIONS.of(code) || code; } catch (e) { return code; } };
const ALL_COUNTRIES = ('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ '
  + 'DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP '
  + 'KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM '
  + 'PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG '
  + 'UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW').split(' ');

function TicketFields({ form, set }) {
  return (
    <>
      <TextField label="Ticket number" value={form.ticket} onChange={set('ticket')} required placeholder="e.g. 123456" slotProps={{ htmlInput: { maxLength: 30 } }}
        helperText="The ticket that asked for this change. It is saved with the change." />
      <TextField label="Reason" value={form.reason} onChange={set('reason')} required multiline minRows={2} slotProps={{ htmlInput: { maxLength: 500 } }}
        helperText="For example the customer and what the address is for." />
    </>
  );
}

function AddIpDialog({ onClose, onAdded }) {
  const [form, setForm] = useState({ ip: '', ticket: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ready = form.ip.trim() && form.ticket.trim() && form.reason.trim();

  const save = async () => {
    setBusy(true); setError(null);
    try { const res = await licensingWhitelistAPI.addIp(form); onAdded(res.data.added); }
    catch (err) { setError(errText(err, 'Could not add the address')); setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Add an IP address</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The address is allowed through the Licensing server's firewall straight away. Only a single address can be added; it is always saved as /32.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="IP address" value={form.ip} onChange={set('ip')} required autoFocus placeholder="203.0.113.5" helperText="IPv4, for example 52.20.30.40. Private addresses are refused." />
          <TicketFields form={form} set={set} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !ready}>{busy ? 'Adding...' : 'Add address'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoveIpDialog({ address, onClose, onRemoved }) {
  const [form, setForm] = useState({ ticket: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ready = form.ticket.trim() && form.reason.trim();

  const save = async () => {
    setBusy(true); setError(null);
    try { const res = await licensingWhitelistAPI.removeIp({ ip: address, ...form }); onRemoved(res.data.removed); }
    catch (err) { setError(errText(err, 'Could not remove the address')); setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Remove {address}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This address is taken off the Licensing server's whitelist straight away. Anyone using it is blocked unless their country is allowed.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TicketFields form={form} set={set} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color="error" onClick={save} disabled={busy || !ready}>{busy ? 'Removing...' : 'Remove address'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function CountriesDialog({ current, onClose, onSaved }) {
  const [chosen, setChosen] = useState(new Set(current));
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ ticket: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const added = [...chosen].filter((c) => !current.includes(c)).sort();
  const removed = current.filter((c) => !chosen.has(c));
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ALL_COUNTRIES.filter((c) => !q || c.toLowerCase() === q || countryName(c).toLowerCase().includes(q)).sort((a, b) => countryName(a).localeCompare(countryName(b)));
  }, [search]);
  const toggle = (c) => { const n = new Set(chosen); if (n.has(c)) n.delete(c); else n.add(c); setChosen(n); };
  const ready = (added.length || removed.length) && chosen.size > 0 && form.ticket.trim() && form.reason.trim();

  const save = async () => {
    setBusy(true); setError(null);
    try { await licensingWhitelistAPI.setCountries({ countries: [...chosen], ...form }); onSaved(added, removed); }
    catch (err) { setError(errText(err, 'Could not change the countries')); setBusy(false); }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Allowed countries</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Traffic from these countries reaches the Licensing server. Removing a country blocks everyone there who is not on the IP list.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField size="small" fullWidth label="Find a country" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1, mt: 1 }} />
        <Box sx={{ maxHeight: 240, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, px: 1, mb: 2 }}>
          {shown.map((c) => (
            <FormControlLabel key={c} sx={{ display: 'flex', m: 0 }} control={<Checkbox size="small" checked={chosen.has(c)} onChange={() => toggle(c)} />} label={`${countryName(c)} (${c})`} />
          ))}
        </Box>
        {(added.length > 0 || removed.length > 0) && (
          <Alert severity={removed.length ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {added.length > 0 && <div>Adding: {added.map(countryName).join(', ')}</div>}
            {removed.length > 0 && <div>Removing: {removed.map(countryName).join(', ')}</div>}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TicketFields form={form} set={set} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !ready}>{busy ? 'Saving...' : 'Save countries'}</Button>
      </DialogActions>
    </Dialog>
  );
}

const ACTIONS = { add_ip: 'Added IP', remove_ip: 'Removed IP', add_country: 'Added country', remove_country: 'Removed country' };
const isIp = (a) => a === 'add_ip' || a === 'remove_ip';

function LicensingWhitelist() {
  const { isAdmin } = usePermissions();
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cur, log] = await Promise.all([licensingWhitelistAPI.get(), licensingWhitelistAPI.audit()]);
      setData(cur.data); setAudit(log.data); setError(null);
    } catch (err) { setError(errText(err, 'Could not read the whitelist')); setData((d) => d || { ips: [], countries: [], ip_set: {} }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.ips || []).filter((i) => !q || [i.address, i.ticket, i.reason, i.added_by].some((v) => (v || '').toLowerCase().includes(q)));
  }, [data, search]);
  useEffect(() => { setPage(0); }, [search]);
  const PER = 25;

  if (!data) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  const set = data.ip_set || {};

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Licensing Whitelist</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Who can reach the Licensing server: the IP addresses and countries allowed through its firewall{set.web_acl ? ` (${set.web_acl}, SLD account)` : ''}. Every change is recorded with its ticket number.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="h6">Allowed countries ({data.countries.length})</Typography>
          {isAdmin() && <Button startIcon={<EditIcon />} onClick={() => setDialog({ type: 'countries' })}>Edit countries</Button>}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {data.countries.map((c) => <Chip key={c} label={`${countryName(c)} (${c})`} size="small" />)}
        </Box>
        {!isAdmin() && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Only Cloud Services administrators can change the countries.</Typography>}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h6">Allowed IP addresses ({data.ips.length})</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField size="small" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ type: 'ip' })}>Add IP address</Button>
          </Box>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell>Address</TableCell><TableCell>Ticket</TableCell><TableCell>Reason</TableCell><TableCell>Added by</TableCell><TableCell>Added</TableCell>{isAdmin() && <TableCell />}</TableRow></TableHead>
            <TableBody>
              {rows.slice(page * PER, page * PER + PER).map((i) => (
                <TableRow key={i.address} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{i.address}</TableCell>
                  <TableCell>{i.ticket || '-'}</TableCell>
                  <TableCell>{i.reason || <Typography variant="caption" color="text.secondary">Added before the portal recorded changes</Typography>}</TableCell>
                  <TableCell>{i.added_by || '-'}</TableCell>
                  <TableCell>{fmtTime(i.added_at)}</TableCell>
                  {isAdmin() && <TableCell align="right"><Tooltip title="Remove this address"><IconButton size="small" aria-label={`Remove ${i.address}`} onClick={() => setDialog({ type: 'remove', address: i.address })}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip></TableCell>}
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6}><Typography color="text.secondary">No addresses match.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={rows.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={PER} rowsPerPageOptions={[PER]} />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Recent changes</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell>When</TableCell><TableCell>Who</TableCell><TableCell>Change</TableCell><TableCell>Ticket</TableCell><TableCell>Reason</TableCell><TableCell>Result</TableCell></TableRow></TableHead>
            <TableBody>
              {audit.slice(0, 20).map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{fmtTime(a.requested_at)}</TableCell>
                  <TableCell>{a.requested_name || a.requested_by}</TableCell>
                  <TableCell>{ACTIONS[a.action]}: {isIp(a.action) ? a.value : `${countryName(a.value)} (${a.value})`}</TableCell>
                  <TableCell>{a.ticket}</TableCell>
                  <TableCell>{a.reason}</TableCell>
                  <TableCell><Chip size="small" label={a.result} color={a.result === 'success' ? 'success' : a.result === 'failed' ? 'error' : 'default'} title={a.error || ''} /></TableCell>
                </TableRow>
              ))}
              {audit.length === 0 && <TableRow><TableCell colSpan={6}><Typography color="text.secondary">No changes made through the portal yet.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {dialog?.type === 'ip' && <AddIpDialog onClose={() => setDialog(null)} onAdded={(ip) => { setDialog(null); setNotice(`${ip} is now allowed through the Licensing server's firewall.`); load(); }} />}
      {dialog?.type === 'remove' && <RemoveIpDialog address={dialog.address} onClose={() => setDialog(null)} onRemoved={(ip) => { setDialog(null); setNotice(`${ip} has been removed from the Licensing server's whitelist.`); load(); }} />}
      {dialog?.type === 'countries' && <CountriesDialog current={data.countries} onClose={() => setDialog(null)}
        onSaved={(a, r) => { setDialog(null); setNotice(`Countries updated${a.length ? `, added: ${a.map(countryName).join(', ')}` : ''}${r.length ? `, removed: ${r.map(countryName).join(', ')}` : ''}.`); load(); }} />}
    </Box>
  );
}

export default LicensingWhitelist;
